"use client"
import { verifyAddress } from "@/actions/loqate/client/verify"
import { paymentFields } from "@/actions/payment/client/payment-fields"
import {
    verificationIssueFieldMessages,
    type AddressVerificationCopy
} from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/addressVerificationCopy"
import {
    formatAddressForDisplay,
    mergeAddressLine
} from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/formatCheckoutAddress"
import AddressVerificationModal from "@/app/(site)/[locale]/checkout/CheckoutContent/modals/AddressVerificationModal"
import AddressForm, {
    AddressFormEnum
} from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/CustomerInformation/CustomerFormFieldGroup/AddressForm"
import { type MarketLayout } from "@/app/(site)/[locale]/checkout/types"
import AnimateHeight from "@/app/(site)/_components/Atoms/AnimateHeight"
import {
    FieldValidationProvider,
    useFieldValidationActions,
    useFieldValidationState
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldValidationRegistry"
import Checkbox from "@/app/(site)/_components/Atoms/Inputs/checkbox"
import ButtonElement from "@/app/(site)/_components/GCMolecules/ButtonElement"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { useGetSameShippingAddress } from "@/app/(site)/_utils/_hooks/useGetSameShippingAddress"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { type CheckoutStep } from "@/app/(site)/contexts/ecommerce/types"
import { useCheckoutFormStore } from "@/app/(site)/contexts/Global"
import {
    addressVerificationKey,
    needsAddressVerification
} from "@/lib/loqate/addressVerificationKey"
import { fallbackVerificationResult } from "@/lib/loqate/fallbackVerification"
import { SAME_SHIPPING_ADDRESS_STORAGE_KEY } from "@/lib/siteconfig/constants"
import {
    CheckoutEvents,
    subscribeToIsSubscribiningKlaviyoEmail,
    unsubscribeFromEvent
} from "@/lib/utils/Events/helpers"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { t } from "@/translations"
import { ResultUtil } from "@/types/generic/Result"
import { type AddressVerificationResult } from "@/types/loqate/verify"
import { useEffect, useRef, useState } from "react"

interface SubmittableAddress {
    email?: string
    firstName: string
    lastName: string
    address1: string
    apartmentHouseNumber: string
    address2?: string
    zipCode: string
    city: string
    country: string
    state?: string
    phoneNumber?: string
}

interface PendingSubmission {
    shippingAddress: SubmittableAddress
    billingAddress: SubmittableAddress
    newsletter: boolean
}

interface CustomerInformationProps {
    setStep: (step: CheckoutStep) => void
    langCode: string
    marketLayout: MarketLayout
    addressVerificationCopy: AddressVerificationCopy
}

export default function CustomerInformation(props: CustomerInformationProps) {
    return (
        <FieldValidationProvider>
            <CustomerInformationForm {...props} />
        </FieldValidationProvider>
    )
}

function CustomerInformationForm({
    setStep,
    langCode,
    marketLayout,
    addressVerificationCopy
}: CustomerInformationProps) {
    const { isLoadingSelection, selection, mutate } = useSWRBasketSessionState()
    /** Non-null: the wrapper above provides the registry every field registers with. */
    const fieldValidation = useFieldValidationActions()
    /** A field is carrying something no correction to the form can fix, e.g. PFAS. */
    const { hasBlockingError } = useFieldValidationState()
    const checkoutCurrentStep = useEcommerceStore(state => state.checkoutCurrentStep)
    const getSameShippingAddress = useGetSameShippingAddress()
    const [sameShippingAddress, setSameShippingAddress] = useState<boolean>(true)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [isSubscribiningKlaviyoEmail, setIsSubscribiningKlaviyoEmail] =
        useState<boolean>(false)
    const [verification, setVerification] = useState<AddressVerificationResult | null>(
        null
    )
    /** Submission held while the verification dialog is open. */
    const pendingSubmission = useRef<PendingSubmission | null>(null)
    /** Field named by the first verification issue, focused once the dialog closes. */
    const focusFieldOnClose = useRef<string | null>(null)

    const verifiedShippingAddressKey = useCheckoutFormStore(
        state => state.verifiedShippingAddressKey
    )
    const setVerifiedShippingAddressKey = useCheckoutFormStore(
        state => state.setVerifiedShippingAddressKey
    )

    // Tracking
    const hasTrackedBeginCheckout = useRef(false)
    const siteContext = useSiteContext()
    useEffect(() => {
        if (
            !hasTrackedBeginCheckout.current &&
            selection &&
            siteContext.site_country &&
            siteContext.site_language
        ) {
            import("@/lib/utils/tracking/gtmEvents").then(({ beginCheckoutEvent }) => {
                beginCheckoutEvent(selection, siteContext)
            })
            hasTrackedBeginCheckout.current = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selection), siteContext.site_country, siteContext.site_language])
    // End tracking

    useEffect(() => {
        setSameShippingAddress(getSameShippingAddress)
    }, [getSameShippingAddress])

    useEffect(() => {
        const handle = subscribeToIsSubscribiningKlaviyoEmail(
            setIsSubscribiningKlaviyoEmail
        )
        return () => {
            if (handle) {
                unsubscribeFromEvent(CheckoutEvents.IS_SUBSCRIBING_KLAVIYO_EMAIL, handle)
            }
        }
    }, [])

    const handleSameShippingAddressChange = (value: boolean) => {
        setSameShippingAddress(value)
        if (typeof window !== "undefined") {
            sessionStorage.setItem(SAME_SHIPPING_ADDRESS_STORAGE_KEY, String(value))
        }
    }

    /** Split out of `onSubmit` so the dialog can resume the same submission. */
    const submitAddresses = async ({
        shippingAddress,
        billingAddress,
        newsletter
    }: PendingSubmission) => {
        setSubmitting(true)

        try {
            const centraResponse = await paymentFields({
                language: langCode,
                vouchers: selection?.discounts?.vouchers || [],
                address: {
                    newsletter,
                    firstName: billingAddress.firstName || null,
                    lastName: billingAddress.lastName || null,
                    address1: mergeAddressLine(billingAddress),
                    address2: billingAddress.address2 || null,
                    phoneNumber: billingAddress.phoneNumber || null,
                    zipCode: billingAddress.zipCode || null,
                    email: billingAddress.email || null,
                    city: billingAddress.city || null,
                    country: billingAddress.country || null,
                    state: billingAddress.state || null
                },
                shippingAddress: {
                    firstName: shippingAddress.firstName || null,
                    lastName: shippingAddress.lastName || null,
                    address1: mergeAddressLine(shippingAddress),
                    address2: shippingAddress.address2 || null,
                    phoneNumber: shippingAddress.phoneNumber || null,
                    zipCode: shippingAddress.zipCode || null,
                    email: shippingAddress.email || null,
                    city: shippingAddress.city || null,
                    country: shippingAddress.country || null,
                    state: shippingAddress.state || null
                }
            })

            if (ResultUtil.isSuccess(centraResponse)) {
                await mutate()
                fieldValidation?.clearReportedErrors()
                setStep(2)
            } else {
                console.error(
                    "CustomerInformation: Failed to update payment fields in Centra",
                    centraResponse.error
                )
                throw new Error("Failed to update payment information")
            }
        } catch (error) {
            console.error(
                "CustomerInformation: Failed to update customer information",
                error
            )
        } finally {
            setSubmitting(false)
        }
    }

    /**
     * The dialog's list, moved onto the fields it names, so the customer still knows what
     * to change once it closes. Each one clears as soon as that field is edited.
     */
    const markVerificationIssuesOnFields = (
        issues: AddressVerificationResult["issues"]
    ) => {
        fieldValidation?.clearReportedErrors()
        for (const { field, message } of verificationIssueFieldMessages(
            issues,
            addressVerificationCopy
        )) {
            fieldValidation?.setFieldErrors(
                `${AddressFormEnum.SHIPPING_ADDRESS}_${field}`,
                [{ code: "address_not_verified", message }]
            )
        }
    }

    const handleKeepEntered = async () => {
        const pending = pendingSubmission.current
        fieldValidation?.clearReportedErrors()
        setVerification(null)

        if (!pending) {
            return
        }

        // Recorded as verified so standing by an address does not reopen the dialog.
        setVerifiedShippingAddressKey(addressVerificationKey(pending.shippingAddress))
        await submitAddresses(pending)
    }

    /**
     * This form stays mounted after continue (the accordion only hides it). Drop leftover
     * flags once they have left this step — country change and the dialog buttons also
     * clear them.
     */
    useEffect(() => {
        if (checkoutCurrentStep !== 1) {
            fieldValidation?.clearReportedErrors()
        }
    }, [checkoutCurrentStep, fieldValidation])

    const handleEditAddress = () => {
        const firstField = verification?.issues.find(item => item.field)?.field
        setVerification(null)
        pendingSubmission.current = null
        // Deferred: the dialog's focus trap is still up and would pull focus straight
        // back, so the failing field is claimed once the dialog releases it.
        focusFieldOnClose.current = firstField ?? "address1"
    }

    /** Runs when the dialog hands focus back, whichever way the customer left it. */
    const handleDialogClosed = () => {
        const field = focusFieldOnClose.current
        if (!field) {
            return
        }

        focusFieldOnClose.current = null
        fieldValidation?.focusField(`${AddressFormEnum.SHIPPING_ADDRESS}_${field}`)
    }

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        event.stopPropagation()
        event.persist()

        const form = event.currentTarget

        // Each field trims, checks itself and reports back; the first to fail takes focus.
        if (!fieldValidation?.validateAll()) {
            return
        }

        const formData = new FormData(form)

        const readAddress = (prefix: string): SubmittableAddress => ({
            email: (formData.get(`${prefix}_email`) as string) || undefined,
            firstName: (formData.get(`${prefix}_firstName`) as string) || "",
            lastName: (formData.get(`${prefix}_lastName`) as string) || "",
            address1: (formData.get(`${prefix}_address1`) as string) || "",
            apartmentHouseNumber:
                (formData.get(`${prefix}_apartmentHouseNumber`) as string) || "",
            address2: (formData.get(`${prefix}_address2`) as string) || undefined,
            zipCode: (formData.get(`${prefix}_zipCode`) as string) || "",
            city: (formData.get(`${prefix}_city`) as string) || "",
            country: (formData.get(`${prefix}_country`) as string) || "",
            state: (formData.get(`${prefix}_state`) as string) || undefined,
            phoneNumber: (formData.get(`${prefix}_phoneNumber`) as string) || undefined
        })

        const shippingAddress = readAddress(AddressFormEnum.SHIPPING_ADDRESS)

        // When same shipping address, copy shipping to billing (not a reference)
        const billingAddress = sameShippingAddress
            ? { ...shippingAddress }
            : readAddress(AddressFormEnum.BILLING_ADDRESS)

        const pending: PendingSubmission = {
            shippingAddress,
            billingAddress,
            newsletter: formData.get("newsletter") === "on"
        }
        pendingSubmission.current = pending

        // Only the shipping address is verified — it is the one that has to be deliverable.
        if (needsAddressVerification(shippingAddress, verifiedShippingAddressKey)) {
            setSubmitting(true)
            const result = await verifyAddress({
                address1: shippingAddress.address1,
                address2: "",
                apartment: shippingAddress.apartmentHouseNumber || undefined,
                city: shippingAddress.city,
                state: shippingAddress.state,
                zipCode: shippingAddress.zipCode,
                country: shippingAddress.country
            })
            setSubmitting(false)

            if (result.decision !== "accept") {
                markVerificationIssuesOnFields(result.issues)
                setVerification(result)
                return
            }

            if (result.verificationUnavailable) {
                if (result.avc === null) {
                    // Loqate never answered, so only our own checks are left and the
                    // address stays unrecorded — the next attempt should ask Loqate again.
                    const fallback = fallbackVerificationResult({
                        address1:
                            mergeAddressLine(shippingAddress) ?? shippingAddress.address1,
                        address2: shippingAddress.apartmentHouseNumber,
                        country: shippingAddress.country
                    })

                    if (fallback) {
                        markVerificationIssuesOnFields(fallback.issues)
                        setVerification(fallback)
                        return
                    }
                }
            } else {
                setVerifiedShippingAddressKey(addressVerificationKey(shippingAddress))
            }
        }

        await submitAddresses(pending)
    }

    return (
        <form onSubmit={onSubmit} className="h-full pb-24" noValidate>
            <AddressForm
                type={AddressFormEnum.SHIPPING_ADDRESS}
                key={AddressFormEnum.SHIPPING_ADDRESS}
                title={t(langCode, "checkout.shipping_address")}
                disabled={isLoadingSelection}
                langCode={langCode}
                marketLayout={marketLayout}
            />
            <AnimateHeight minified={sameShippingAddress} removeFromDomWhenClosed={true}>
                <AddressForm
                    type={AddressFormEnum.BILLING_ADDRESS}
                    key={AddressFormEnum.BILLING_ADDRESS}
                    title={t(langCode, "checkout.billing_address")}
                    disabled={isLoadingSelection}
                    required={!sameShippingAddress}
                    langCode={langCode}
                    marketLayout={marketLayout}
                    inputWrapperClassName="pt-40"
                />
            </AnimateHeight>

            <Checkbox
                name="sameShippingAddress"
                label={t(langCode, "checkout.billing_address_same_as_shipping")}
                defaultValue={sameShippingAddress}
                onChange={e => {
                    handleSameShippingAddressChange(e.target.checked)
                }}
                inseta11y={{
                    before: "before:absolute before:inset-0 before:-inset-x-4 before:-inset-y-4 before:content-['']"
                }}
                labelClassName="mt-20 mb-24" //margin here because added a11y click area
            />

            <ButtonElement
                text={t(langCode, "checkout.continue_to_shipping")}
                disabled={
                    isLoadingSelection ||
                    submitting ||
                    isSubscribiningKlaviyoEmail ||
                    hasBlockingError
                }
                size={{ desktop: "large", mobile: "small" }}
                theme="primary-black"
                type="submit"
                className="w-full justify-center"
            />

            <AddressVerificationModal
                result={verification}
                copy={addressVerificationCopy}
                enteredAddressLines={
                    pendingSubmission.current
                        ? formatAddressForDisplay(
                              pendingSubmission.current.shippingAddress
                          )
                        : []
                }
                onKeepEntered={handleKeepEntered}
                onEditAddress={handleEditAddress}
                onClosed={handleDialogClosed}
            />
        </form>
    )
}
